/**
 * Merges multiple class names into a single string, filtering out falsy values and applying Tailwind CSS utility merging.
 * Combines `clsx` for class name handling with `twMerge` for Tailwind-specific class merging.
 *
 * @param {...(string|boolean|undefined|null|Array<string|boolean|undefined|null>|Record<string, boolean|undefined|null>)} inputs - Class names or conditional class objects/arrays.
 * @returns {string} A single string of merged class names with conflicts resolved.
 * @example
 * cn('text-red', ['bg-blue', false, 'p-4'], { 'hidden': false, 'block': true }) // Returns 'text-red bg-blue p-4 block'
 */
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Configuration array defining fields for article and issue settings.
 * Each field specifies metadata properties, types, and behaviors for use in a publication management system.
 *
 * @typedef {Object} FieldConfig
 * @property {string} property - Display name of the field (e.g., "Journal", "Volume").
 * @property {string} key - Unique identifier for the field (e.g., "journal", "volume").
 * @property {"article"|"issue"|"both"} articleOrIssue - Scope of the field (article, issue, or both).
 * @property {string} method - Data type or input method (e.g., "string", "number", "date", "dropdown", "yes/no").
 * @property {"imported"|"calculated"|"manual"} fieldType - Source of the field value (imported, calculated, or manual).
 * @property {boolean} manualOverwrite - Indicates if the field can be manually overwritten.
 *
 * @type {FieldConfig[]}
 * @example
 * fieldConfig[0] // { property: "Journal", key: "journal", articleOrIssue: "both", method: "string", fieldType: "imported", manualOverwrite: false }
 */
let fieldConfig = [
  {
    property: "Journal",
    key: "journalName",
    articleOrIssue: "both",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "PTS#",
    key: "pts",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "number",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Item group",
    key: "itemGroup",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Volume",
    key: "volume",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "number",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Issue",
    key: "issue",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "number",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Vol/Iss",
    key: "volIss",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "EM#",
    key: "em",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "DOI",
    key: "doi",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "PII",
    key: "pii",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "URL",
    key: "url",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "PIT",
    key: "pit",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "DocHead",
    key: "docHead",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Production workflow",
    key: "productionWorkflow",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "dropdown",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Points",
    key: "points",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "number",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Production handler",
    key: "productionHandler",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "dropdown",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Handling editor",
    key: "handlingEditor",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Title",
    key: "title",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "First author",
    key: "firstAuthor",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Corr. author",
    key: "correspondingAuthor",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Corr. author email",
    key: "correspondingAuthorEmail",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Consortia",
    key: "consortia",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "string",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Received",
    key: "receivedDate",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Revised",
    key: "revisedDate",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Pre-accept",
    key: "preAcceptDate",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Accepted",
    key: "acceptedDate",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Login complete",
    key: "loginComplete",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "ID pass",
    key: "status",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "ID notes",
    key: "idNotes",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "string",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Date back from SCE",
    key: "dateBackFromSce",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "SCE returned status",
    key: "sceReturnedStatus",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Assigned date",
    key: "assignedDate",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Speed target",
    key: "speedTarget",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Fast-track",
    key: "fastTrack",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "On completion",
    key: "completionReference",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "ON date",
    key: "onDate",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Embargo exp",
    key: "embargoExp",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date/time",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Embargo stg",
    key: "embargoStg",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Article S300 date",
    key: "articleS300Date",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Item holdout",
    key: "itemHoldout",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Item finalizing",
    key: "itemFinalizing",
    articleOrIssue: "article",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Welcome letter",
    key: "welcomeLetter",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Press release?",
    key: "pressRelease",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "LE figures ready",
    key: "leFiguresReady",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "CE/QC review complete",
    key: "ceQcReviewComplete",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Saved to tipsheet folder",
    key: "savedToTipsheetFolder",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "PTS record check",
    key: "ptsRecordCheck",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Sent pub date to author",
    key: "sentPubDateToAuthor",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Copy edit task complete",
    key: "copyEditTaskComplete",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Proofs to author",
    key: "proofsToAuthor",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "AU proof correx returned",
    key: "auProofCorrectionsSubmitted",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Revised proof requested",
    key: "revisedProofRequested",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "CFC task complete",
    key: "cfcTaskComplete",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "AITI task completed",
    key: "aitiTaskCompleted",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Revised proof approved",
    key: "revisedProofApproved",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Sent final email to author",
    key: "sentFinalEmailToAuthor",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "PTS milestone",
    key: "ptsMilestone",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "EW imported S5",
    key: "ewImportedS5",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "SD published on the web S5",
    key: "sdPublishedOnWebS5",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "EW imported S200",
    key: "ewImportedS200",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "SD published on the web S200",
    key: "sdPublishedOnWebS200",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "SD published on the web S250",
    key: "sdPublishedOnWebS250",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "SD published on the web S300",
    key: "sdPublishedOnWebS300",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "OA status",
    key: "oaStatus",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Free featured online",
    key: "freeFeaturedOnline",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Cover sub received",
    key: "coverSubReceived",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "TOC order position",
    key: "tocOrderPosition",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "number",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "PTS refers to",
    key: "ptsRefersTo",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "PTS remarks",
    key: "ptsRemarks",
    articleOrIssue: "article",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Post-acceptance notes",
    key: "productionNotes",
    articleOrIssue: "article",
    method: "manual",
    fieldType: "string",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Issue S300 date",
    key: "issueS300Date",
    articleOrIssue: "issue",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Last MS for SCE",
    key: "lastMsForSce",
    articleOrIssue: "issue",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Issue holdout",
    key: "issueHoldout",
    articleOrIssue: "issue",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Issue finalizing",
    key: "issueFinalizing",
    articleOrIssue: "issue",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "PII",
    key: "pii",
    articleOrIssue: "issue",
    method: "imported",
    fieldType: "string",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Cover handler",
    key: "coverHandler",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "dropdown",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Cover sub invites",
    key: "coverSubInvites",
    articleOrIssue: "issue",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Cover invite sent",
    key: "coverInviteSent",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "yes/no",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Cover sub due",
    key: "coverSubDue",
    articleOrIssue: "issue",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Cover due to supplier",
    key: "coverDueToSupplier",
    articleOrIssue: "issue",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Cover sent to supplier",
    key: "coverSentToSupplier",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Cover license in PTS",
    key: "coverLicenseInPts",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Cover proof approved",
    key: "coverProofApproved",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "TOC handler",
    key: "tocHandler",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "dropdown",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "TOC order from editors",
    key: "tocOrderFromEditors",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "TOC to supplier",
    key: "tocToSupplier",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Finalizing handler",
    key: "finalizingHandler",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "dropdown",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Issue details approved",
    key: "issueDetailsApproved",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "All papers finalized",
    key: "allPapersFinalized",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "Issue delivered to EW",
    key: "issueDeliveredToEw",
    articleOrIssue: "issue",
    method: "imported",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "Web handler",
    key: "webHandler",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "dropdown",
    manualOverwrite: true,
    showField: false,
  },
  {
    property: "st@r form due",
    key: "strFormDue",
    articleOrIssue: "issue",
    method: "calculated",
    fieldType: "date",
    manualOverwrite: false,
    showField: false,
  },
  {
    property: "st@r form submitted",
    key: "strFormSubmitted",
    articleOrIssue: "issue",
    method: "manual",
    fieldType: "date",
    manualOverwrite: true,
    showField: false,
  },
];
/**
 * Array of header configurations for article display in a publication management system.
 * Each header maps a field key to a display value for use in tables or UI components.
 *
 * @typedef {Object} ArticleHeader
 * @property {string} key - Unique identifier for the field (e.g., "pts", "title").
 * @property {string} value - Display name for the field (e.g., "PTS ID", "TITLE").
 *
 * @type {ArticleHeader[]}
 * @example
 * ArticleHeaders[0] // { key: "pts", value: "PTS ID" }
 */
let ArticleHeaders = [
  { key: "pts", value: "PTS#" },
  { key: "volIssue", value: "VOL/ISS" },
  { key: "em", value: "EM#" },
  { key: "firstAuthor", value: "FIRST AU" },
  { key: "correspondingAuthor", value: "CORR AU" },
  { key: "docHead", value: "DOCHEAD" },
  { key: "journal", value: "JOURNAL" },
  { key: "team", value: "TEAM" },
  { key: "pit", value: "PIT" },
  { key: "title", value: "TITLE" },
  { key: "handlingEditor", value: "EDITOR" },
  { key: "status", value: "ID PASS" },
  { key: "productionNotes", value: "POST-ACC NOTES" },
  { key: "ptsRemarks", value: "REMARKS" },
  { key: "speedTarget", value: "SPEED TARGET" },
  { key: "onDate", value: "ON DATE" },
  { key: "itemGroup", value: "ITEM GROUP" },
  { key: "volume", value: "VOLUME" },
  { key: "issue", value: "ISSUE" },
  { key: "correspondingAuthorEmail", value: "CORR AU EMAIL" },
  { key: "doi", value: "DOI" },
  { key: "pii", value: "PII" },
  { key: "url", value: "URL" },
  { key: "ptsRefersTo", value: "REFERS TO" },
  { key: "idNotes", value: "ID NOTES" },
  { key: "welcomeLetter", value: "WELCOME" },
  { key: "pressRelease", value: "PR" },
  { key: "leFiguresReady", value: "LE FIGS" },
  { key: "ceQcReviewComplete", value: "QCED" },
  { key: "savedToTipsheetFolder", value: "TIPSHEET" },
  { key: "ptsRecordCheck", value: "PTS CHECK" },
  { key: "sentPubDateToAuthor", value: "PUB DATE TO AU" },
  { key: "sentFinalEmailToAuthor", value: "FIN EM TO AU" },
  { key: "consortia", value: "CONSORTIA" },
  { key: "fastTrack", value: "FT" },
  { key: "freeFeaturedOnline", value: "FFA" },
  { key: "coverSubReceived", value: "COV SUB REC" },
  { key: "tocOrderPosition", value: "TOC POS" },
  { key: "oaStatus", value: "OA" },
  { key: "copyEditTaskComplete", value: "CE CLOSED" },
  { key: "proofsToAuthor", value: "PROOFS TO AU" },
  { key: "auProofCorrectionsSubmitted", value: "AU CX REC" },
  { key: "revisedProofRequested", value: "RPR" },
  { key: "cfcTaskComplete", value: "CFC CLOSED" },
  { key: "aitiTaskCompleted", value: "AITI CLOSED" },
  { key: "revisedProofApproved", value: "REV OK" },
  { key: "receivedDate", value: "RECEIVED" },
  { key: "revisedDate", value: "REVISED" },
  { key: "preAcceptDate", value: "PRE-ACCEPT" },
  { key: "acceptedDate", value: "ACCEPTED" },
  { key: "loginComplete", value: "COMPLETED" },
  { key: "dateBackFromSce", value: "DATE FROM SCE" },
  { key: "sceReturnedStatus", value: "SCE BACK" },
  { key: "assignedDate", value: "ASSIGN DATE" },
  { key: "completionReference", value: "PTOC" },
  { key: "ptsMilestone", value: "MILESTONE" },
  { key: "ewImportedS5", value: "EW IMP S5" },
  { key: "sdPublishedOnWebS5", value: "SD PUB S5" },
  { key: "ewImportedS200", value: "EW IMP S200" },
  { key: "sdPublishedOnWebS200", value: "SD PUB S200" },
  { key: "sdPublishedOnWebS250", value: "SD PUB S250" },
  { key: "sdPublishedOnWebS300", value: "SD PUB S300" },
  { key: "embargoExp", value: "EMB EXP" },
  { key: "embargoStg", value: "EMB STG" },
  { key: "articleS300Date", value: "S300 DATE" },
  { key: "itemHoldout", value: "HOLDOUT" },
  { key: "itemFinalizing", value: "FINALIZING" },
  { key: "productionWorkflow", value: "PROD WF" },
];

const filteredArticleHeaders = [
  //   { key: "volIss", value: "VOL/ISS" },
  { key: "docHead", value: "DOCHEAD" },
  { key: "pts", value: "PTS#" },
  { key: "correspondingAuthor", value: "CORR. AUTHOR" },
  { key: "ptsMilestone", value: "PTS MILESTONE" },
  { key: "productionHandler", value: "PRODUCTION HANDLER" },
  { key: "tocOrderPosition", value: "TOC ORDER POSITION" },
  //   { key: "em", value: "EM#" },
  //   { key: "doi", value: "DOI" },
  //   { key: "correspondingAuthorEmail", value: "CORR. AUTHOR EMAIL" },
  //   { key: "sceReturnedStatus", value: "SCE RETURNED STATUS" },
  //   { key: "onDate", value: "ON DATE" },
  //   { key: "copyEditTaskComplete", value: "COPY EDIT TASK COMPLETE" },
  //   { key: "proofsToAuthor", value: "PROOFS TO AUTHOR" },
  //   { key: "auProofCorrectionsSubmitted", value: "AU PROOF CORREX SUBMITTED" },
  //   { key: "revisedProofRequested", value: "REVISED PROOF REQUESTED" },
  //   { key: "cfcTaskComplete", value: "CFC TASK COMPLETE" },
  //   { key: "aitiTaskCompleted", value: "AITI TASK COMPLETED" },
  //   { key: "revisedProofApproved", value: "REVISED PROOF APPROVED" },
  //   { key: "sdPublishedOnWebS5", value: "SD PUBLISHED TO THE WEB S5" },
  //   { key: "sdPublishedOnWebS200", value: "SD PUBLISHED TO THE WEB S200" },
  //   { key: "sdPublishedOnWebS250", value: "SD PUBLISHED TO THE WEB S250" },
  //   { key: "sdPublishedOnWebS300", value: "SD PUBLISHED TO THE WEB S300" },
  //   { key: "freeFeaturedOnline", value: "FREE FEATURED ONLINE" },
  //   { key: "coverSubReceived", value: "COVER SUB RECEIVED" },
  //   { key: "ptsRefersTo", value: "PTS REFERS TO" },
];

export { fieldConfig, ArticleHeaders, filteredArticleHeaders };
